import { useCallback, useEffect, useRef, useState, type TouchEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ContentItem } from '@/types/content';

interface HeroCarouselProps {
  items: ContentItem[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);
  const navigate = useNavigate();
  const total = items.length;

  const goTo = useCallback((next: number) => {
    setIndex(((next % total) + total) % total);
  }, [total]);

  useEffect(() => {
    if (total <= 1) return undefined;
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % total);
    }, 5500);
    return () => clearInterval(timer);
  }, [total]);

  if (total === 0) return null;

  const handleTouchStart = (event: TouchEvent) => {
    touchStartX.current = event.touches[0].clientX;
  };

  const handleTouchEnd = (event: TouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = event.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 50) {
      goTo(delta < 0 ? index + 1 : index - 1);
    }
    touchStartX.current = null;
  };

  return (
    <section className="relative w-full">
      <div
        className="relative w-full h-[440px] md:h-[600px] overflow-hidden bg-primary-950"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {items.map((item, i) => (
          <div
            key={item.id}
            className={`absolute inset-0 transition-opacity duration-700 ${
              i === index ? 'opacity-100' : 'opacity-0 pointer-events-none'
            }`}
          >
            <img
              src={item.backdrop}
              alt={`${item.title} 推荐海报`}
              title={`${item.title} - 91大神影视大全`}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-primary-950/90 via-primary-950/60 to-primary-950/20" />
            <div className="absolute inset-0 bg-gradient-to-t from-primary-950/90 via-transparent to-primary-950/40" />
          </div>
        ))}

        <div className="relative z-10 h-full w-full px-4 md:px-8 flex items-center">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent-500/90 text-background-50 text-xs font-medium mb-4">
              <i className="ri-fire-line text-sm" />
              本周焦点推荐
            </span>
            <h1 className="font-heading font-extrabold text-3xl md:text-5xl text-background-50 leading-tight">
              {items[index].title}
            </h1>
            <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-background-100/90">
              <span className="flex items-center gap-1">
                <i className="ri-star-fill text-accent-400" />
                {items[index].rating.toFixed(1)}
              </span>
              <span>{items[index].year}</span>
              <span>{items[index].region}</span>
              <span>{items[index].genres.join(' · ')}</span>
            </div>
            <p className="mt-4 text-sm md:text-base text-background-100/80 leading-relaxed line-clamp-2 md:line-clamp-3 max-w-xl">
              {items[index].description}
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => navigate(`/detail/${items[index].id}`)}
                className="flex items-center gap-2 px-5 md:px-6 py-2.5 md:py-3 rounded-md bg-accent-500 text-background-50 font-medium text-sm hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-play-fill text-lg" />
                查看详情
              </button>
              <button
                type="button"
                onClick={() => navigate(`/category/${encodeURIComponent(items[index].type)}`)}
                className="px-5 md:px-6 py-2.5 md:py-3 rounded-md bg-background-50/10 border border-background-50/40 text-background-50 font-medium text-sm hover:bg-background-50/20 transition-colors whitespace-nowrap cursor-pointer"
              >
                更多{items[index].type}
              </button>
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={() => goTo(index - 1)}
          className="hidden md:flex absolute left-6 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-background-50/20 text-background-50 items-center justify-center hover:bg-background-50/30 transition-colors cursor-pointer"
          aria-label="上一张"
        >
          <i className="ri-arrow-left-s-line text-2xl" />
        </button>
        <button
          type="button"
          onClick={() => goTo(index + 1)}
          className="hidden md:flex absolute right-6 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-background-50/20 text-background-50 items-center justify-center hover:bg-background-50/30 transition-colors cursor-pointer"
          aria-label="下一张"
        >
          <i className="ri-arrow-right-s-line text-2xl" />
        </button>

        <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
          {items.map((item, i) => (
            <button
              key={item.id}
              type="button"
              onClick={() => goTo(i)}
              className={`h-1.5 rounded-full transition-all cursor-pointer ${
                i === index ? 'w-7 bg-accent-500' : 'w-3 bg-background-50/50'
              }`}
              aria-label={`切换到第${i + 1}张`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}